import {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useMemo,
} from "react"

import lodash from "lodash"
import { createPortal } from "react-dom"
import { useControl, type IControl } from "react-map-gl/mapbox"

import { StabiloControls } from "../../components/StabiloControls"
import { SELECTED_LINE_LAYER_SIZE } from "../../constants/layers"
import { getLineLayerConfig, getInterpolatedLineLayerWidth } from "../../utils/layersConfigs"

import { StabiloDraw } from "../StabiloDraw"

type DrawControlProps = {
    container?: HTMLElement | null
    displayControlsDefault?: boolean
    defaultMode?: string
    currentMode: string
    modes?: Record<string, any>
    lineColorStyle?: string
    isDirty?: boolean
    onAdded: () => void
    onUpdate: (_event: any) => void
    onModeChange: (_mode: string) => void
    onRestore: () => void
}

export const DrawControl = forwardRef((props: DrawControlProps, ref) => {

    const {
        container,
        defaultMode,
        displayControlsDefault = false,
        onAdded,
        onUpdate,
        currentMode,
        modes,
        isDirty = false,
        onModeChange,
        onRestore,
        lineColorStyle,
        ...rest
    } = props

    const paintedLayerConfig = useMemo(() => {
        return lodash.merge(
            getLineLayerConfig(),
            {
                "paint": {
                    "line-color": lineColorStyle,
                    "line-width": getInterpolatedLineLayerWidth(SELECTED_LINE_LAYER_SIZE),
                },
            },
        )
    }, [lineColorStyle])

    const handleOnAdded = useCallback(() => {
        onAdded()
    }, [onAdded])

    const drawRef = useControl(
        () => {
            const draw = new StabiloDraw({
                props: {
                    styles: [
                        {
                            "id": "painted-line",
                            ...paintedLayerConfig,
                        },
                    ],
                    defaultMode,
                    displayControlsDefault,
                    modes: Object.entries(modes).reduce((acc, [key, { mode }]) => {
                        acc[key] = mode;
                        return acc;
                    }, {}),
                    ...rest
                },
                onAdded: handleOnAdded,
            })
            return draw as unknown as IControl
        },
        ({ map }) => {
            onUpdate && map.on("draw.paint_updated", onUpdate)
        },
        ({ map }) => {
            onUpdate && map.off("draw.paint_updated", onUpdate)
        },
    )

    useImperativeHandle(ref, () => drawRef, [drawRef])

    return (
        <div>
            {
                container && createPortal(
                    <StabiloControls
                        modes={modes}
                        mode={currentMode}
                        onChange={onModeChange}
                        onRestore={onRestore}
                        restorable={isDirty}
                    />,
                    container)
            }
        </div>
    )
})
